<script>
  import { onMount } from 'svelte';
  import { writable } from 'svelte/store';
  import HomePage from './routes/HomePage.svelte';
  import PublicContentPage from './routes/PublicContentPage.svelte';
  import EditorV2Page from './routes/EditorV2Page.svelte';
  import EditorV2FeatureListPage from './routes/EditorV2FeatureListPage.svelte';
  import EditorV2OptionAreaPage from './routes/EditorV2OptionAreaPage.svelte';
  import EditorV2HistoryAreaPage from './routes/EditorV2HistoryAreaPage.svelte';
  import EditorV2ImageAreaPage from './routes/EditorV2ImageAreaPage.svelte';
  import EditorV2UploadPage from './routes/EditorV2UploadPage.svelte';
  import DashboardPage from './routes/DashboardPage.svelte';
  import FinetuningPage from './routes/FinetuningPage.svelte';
  import BatchPage from './routes/BatchPage.svelte';
  import SettingsPage from './routes/SettingsPage.svelte';
  import LoginPage from './routes/LoginPage.svelte';
  import PrivacyPolicyPage from './routes/PrivacyPolicyPage.svelte';
  import ActiveUsersPage from './routes/ActiveUsersPage.svelte';
  import ApiDocsPage from './routes/ApiDocsPage.svelte';
  import CommunityPage from './routes/CommunityPage.svelte';
  import DogChatPage from './routes/DogChatPage.svelte';
  import Modal from './components/Modal.svelte';
  import LoginPanel from './components/LoginPanel.svelte';
  import ServiceNotice from './components/ServiceNotice.svelte';
  import { loginGate, rewardNotice, finishLogin, requestLogin, watchAuthentication } from './utils/authSession';
  import { startServiceMonitor } from './utils/serviceAvailability';
  import { refreshCosts } from './stores/costStore';
  import CockerPage from './routes/CockerPage.svelte';
  import AppHeader from './components/AppHeader.svelte';
  import LoadingOverlay from './components/LoadingOverlay.svelte';
  import { settingsStore, editStore, sessionStore, imageStore } from './stores';
  import { get } from 'svelte/store';
  import { locale, t, publicPath } from './i18n';
  import { browserLocale, matchLocale } from './i18n/locales.js';
  import { publicPages, resolvePublicRoute } from './content/publicRoutes.js';
  import { updatePageMetadata } from './content/publicMetadata.js';
  
  // 테마 상태 관리
  // 기본 테마를 라이트 모드로 변경
  export const theme = writable(localStorage.getItem('theme') || 'light');
  
  // 현재 페이지 상태 관리
  let currentPage = resolvePublicRoute(window.location.pathname)?.page || 'home';
  let hideHeader = false;
  let showHeaderOnly = false;
  let settingsInitialTab = 'profile';
  
  // 테마 변경 함수
  function toggleTheme() {
    theme.update(currentTheme => {
      const newTheme = currentTheme === 'dark' ? 'light' : 'dark';
      localStorage.setItem('theme', newTheme);
      
      // 테마 전환 애니메이션 적용
      document.body.classList.add('theme-transition');
      setTimeout(() => {
        document.body.classList.remove('theme-transition');
      }, 600);
      
      // 설정 저장
      settingsStore.update(store => ({
        ...store,
        isDarkMode: newTheme === 'dark'
      }));
      
      return newTheme;
    });
  }
  
  // 페이지 변경 함수
  // 로그인이 필요한 페이지 목록
  const restrictedPages = ['dashboard', 'dog-chat', 'finetuning', 'batch'];

  function updateFromUrl() {
    settingsStore.update(settings => ({ ...settings, language: browserLocale(settings.language) }));
    const path = window.location.pathname;
    const publicRoute = resolvePublicRoute(path);
    if (publicRoute) {
      currentPage = publicRoute.page || 'home';
    } else if (path.includes('community') || path.includes('explore')) {
      currentPage = 'community';
    } else if (path.includes('dog-chat')) {
      currentPage = 'dog-chat';
    } else if (path.includes('editor2/features')) {
      currentPage = 'editorv2-feature-list';
    } else if (path.includes('editor2/options')) {
      currentPage = 'editorv2-option-area';
    } else if (path.includes('editor2/history')) {
      currentPage = 'editorv2-history-area';
    } else if (path.includes('editor2/image')) {
      currentPage = 'editorv2-image-area';
    } else if (path.includes('editor2/upload')) {
      currentPage = 'editorv2-upload';
    } else if (path.includes('editor2')) {
      currentPage = 'editorv2';
    } else if (path.includes('editor')) {
      const newPath = '/editorv2';
      window.history.replaceState({}, '', newPath + window.location.search);
      currentPage = 'editorv2';
    } else if (path.includes('dashboard')) {
      currentPage = 'dashboard';
    } else if (path.includes('finetuning')) {
      currentPage = 'finetuning';
    } else if (path.includes('batch')) {
      currentPage = 'batch';
    } else if (path.includes('login')) {
      currentPage = 'login';
    } else if (path.includes('settings')) {
      currentPage = 'settings';
    } else if (path.includes('privacy')) {
      currentPage = 'privacy';
    } else if (path.includes('active-users')) {
      currentPage = 'active-users';
    } else if (path.includes('api-docs')) {
      currentPage = 'api-docs';
    } else if (path.includes('cocker')) {
      currentPage = 'cocker';
    } else {
      currentPage = 'home';
    }

    const params = new URLSearchParams(window.location.search);
    hideHeader = params.get('hideHeader') === 'true' || currentPage === 'cocker';
    showHeaderOnly = params.get('component') === 'header';
  }
  $: if (typeof document !== 'undefined') {
    updatePageMetadata(currentPage === 'home' ? '' : publicPages.includes(currentPage) ? currentPage : null,
      $locale, import.meta.env.VITE_SITE_URL || undefined);
  }

  async function navigateTo(page, tab = 'profile') {
    if (restrictedPages.includes(page) && !$sessionStore.loggedIn) {
      if (!await requestLogin('이 화면을 이용하려면 로그인해 주세요.')) return;
    }

    if (page === 'settings') {
      settingsInitialTab = tab;
    }

    currentPage = page;
    const params = new URLSearchParams(window.location.search);
    let pathName = page;
    if (page === 'editorv2-feature-list') pathName = 'editor2/features';
    else if (page === 'editorv2-option-area') pathName = 'editor2/options';
    else if (page === 'editorv2-history-area') pathName = 'editor2/history';
    else if (page === 'editorv2-image-area') pathName = 'editor2/image';
    else if (page === 'editorv2-upload') pathName = 'editor2/upload';
    else if (page === 'privacy') pathName = 'privacy';
    else if (page === 'active-users') pathName = 'active-users';
    else if (page === 'api-docs') pathName = 'api-docs';
    else if (page === 'cocker') pathName = 'cocker';
    const isPublicPage = page === 'home' || publicPages.includes(page);
    if (isPublicPage) params.delete('lang');
    else params.set('lang', $locale);
    const path = isPublicPage ? publicPath($locale, page === 'home' ? '' : page) : `/${pathName}`;
    const query = params.toString();
    window.history.pushState({}, '', path + (query ? `?${query}` : ''));
    updateFromUrl();
    window.scrollTo(0, 0);
  }

  function handleInternalLink(event) {
    if (event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
    const anchor = event.target instanceof Element ? event.target.closest('a[href]') : null;
    if (!anchor || anchor.hasAttribute('download') || (anchor.target && anchor.target !== '_self')) return;
    const url = new URL(anchor.href, window.location.href);
    if (url.origin !== window.location.origin || url.hash) return;
    const route = resolvePublicRoute(url.pathname);
    const existingPage = { '/editorv2': 'editorv2', '/editor2': 'editorv2', '/settings': 'settings' }[url.pathname.replace(/\/$/, '')];
    if (!route && !existingPage) return;
    event.preventDefault();
    const language = route?.locale || matchLocale(url.searchParams.get('lang'));
    if (language) settingsStore.update(settings => ({ ...settings, language }));
    navigateTo(route ? route.page || 'home' : existingPage);
  }
  
  // 브라우저 뒤로가기 처리
  onMount(() => {
    updateFromUrl();

    const stopAuthentication = watchAuthentication();
    const stopService = startServiceMonitor();
    refreshCosts();
    const popstateHandler = () => updateFromUrl();
    window.addEventListener('popstate', popstateHandler);

    // 네비게이션 이벤트 리스너 추가
    const navigateHandler = (event) => {
      if (event.detail) {
        navigateTo(event.detail);
      }
    };
    document.addEventListener('navigate', navigateHandler);
    document.addEventListener('click', handleInternalLink);
    
    // 테마 적용
    const unsubscribeTheme = theme.subscribe(value => {
      document.body.className = value;
    });
    
    // 저장된 설정 불러오기
    const savedSettings = localStorage.getItem('ilieon_settings');
    if (savedSettings) {
      try {
        const settings = JSON.parse(savedSettings);
        if (settings.isDarkMode !== undefined) {
          const newTheme = settings.isDarkMode ? 'dark' : 'light';
          theme.set(newTheme);
        }
      } catch (e) {
        console.error('설정 불러오기 오류:', e);
      }
    }
    
    // 설정 변경 시 저장
    const unsubscribe = settingsStore.subscribe(settings => {
      localStorage.setItem('ilieon_settings', JSON.stringify(settings));
    });

    // 모든 페이지에서 작업 완료 시 로딩 상태 해제
    const globalTaskHandler = (event) => {
      const { resultImage, id } = event.detail || {};
      if (id !== get(editStore).currentTaskId) return;
      if (resultImage) {
        imageStore.update(store => ({ ...store, resultImage }));
      }
      editStore.update(store => ({ ...store, isLoading: false }));
    };
    document.addEventListener('taskCompleted', globalTaskHandler);

    return () => {
      stopAuthentication();
      stopService();
      window.removeEventListener('popstate', popstateHandler);
      unsubscribe();
      unsubscribeTheme();
      document.removeEventListener('taskCompleted', globalTaskHandler);
      document.removeEventListener('navigate', navigateHandler);
      document.removeEventListener('click', handleInternalLink);
    };
  });
</script>

<main class={$theme}>
  {#if showHeaderOnly}
    <AppHeader {navigateTo} {currentPage} {toggleTheme} />
  {:else}
    {#if !hideHeader}
      <AppHeader {navigateTo} {currentPage} {toggleTheme} />
    {/if}

  <ServiceNotice />
  {#if $rewardNotice}<div class="reward-notice" role="status">{$rewardNotice}<button aria-label="출석 안내 닫기" on:click={() => rewardNotice.set('')}>×</button></div>{/if}
  <div
    class="content"
    class:editor-page={currentPage.startsWith('editorv2')}
    class:cocker-route={currentPage === 'cocker'}
  >
    {#if restrictedPages.includes(currentPage) && !$sessionStore.loggedIn}
      <LoginPanel />
    {:else if currentPage === 'community'}
      <CommunityPage />
    {:else if currentPage === 'dog-chat'}
      <DogChatPage />
    {:else if currentPage === 'home'}
      <HomePage {navigateTo} />
    {:else if publicPages.includes(currentPage)}
      <PublicContentPage page={currentPage} />
    {:else if currentPage === 'editorv2'}
      <EditorV2Page />
      {#if $editStore.isLoading && !$editStore.hideOverlayForCurrentTask}
        <LoadingOverlay />
      {/if}
    {:else if currentPage === 'editorv2-feature-list'}
      <EditorV2FeatureListPage />
    {:else if currentPage === 'editorv2-option-area'}
      <EditorV2OptionAreaPage />
    {:else if currentPage === 'editorv2-history-area'}
      <EditorV2HistoryAreaPage />
    {:else if currentPage === 'editorv2-image-area'}
      <EditorV2ImageAreaPage />
    {:else if currentPage === 'editorv2-upload'}
      <EditorV2UploadPage />
    {:else if currentPage === 'dashboard'}
      <DashboardPage />
    {:else if currentPage === 'finetuning'}
      <FinetuningPage />
    {:else if currentPage === 'batch'}
      <BatchPage />
    {:else if currentPage === 'settings'}
      <SettingsPage {navigateTo} {toggleTheme} theme={$theme} activeTab={settingsInitialTab} />
    {:else if currentPage === 'login'}
      <LoginPage />
    {:else if currentPage === 'privacy'}
      <PrivacyPolicyPage />
    {:else if currentPage === 'active-users'}
      <ActiveUsersPage />
    {:else if currentPage === 'api-docs'}
      <ApiDocsPage />
    {:else if currentPage === 'cocker'}
      <CockerPage />
    {/if}
  </div>

  {#if currentPage !== 'cocker' && !currentPage.startsWith('editorv2')}
    <br>
    <br>
    <br>
  {/if}

  {#if !currentPage.startsWith('editorv2') && currentPage !== 'cocker'}
  <footer>
    <div class="footer-content">
      <div class="footer-logo">
        <img src="/images/e_rion_logo.png" alt="이리온 로고" class="footer-logo-img" />
      </div>
      
      <div class="footer-links">
        <a class="footer-link-btn" href={publicPath($locale, 'about')}>{$t('about')}</a>
        <a class="footer-link-btn" href={publicPath($locale, 'photo-ideas')}>{$t('photo-ideas')}</a>
        <a class="footer-link-btn" href={publicPath($locale, 'faq')}>{$t('faq')}</a>
        <button type="button" class="footer-link-btn" on:click={() => navigateTo('api-docs')}>{$t('apiDocs')}</button>
        <button type="button" class="footer-link-btn" on:click={() => navigateTo('active-users')}>{$t('liveUsers')}</button>
        <button type="button" class="footer-link-btn" on:click={() => window.open('https://pond-echidna-ecb.notion.site/E-RION-2bf82c606e4180f7b988cfced4b47013', '_blank')}>{$t('privacy')}</button>
        <button type="button" class="footer-link-btn" on:click={() => window.open('https://pond-echidna-ecb.notion.site/E-RION-2bf82c606e4180928714fac2b9efbded', '_blank')}>{$t('terms')}</button>
        <button type="button" class="footer-link-btn" on:click={() => navigateTo('settings', 'contact')}>{$t('contact')}</button>
      </div>
      
      <div class="footer-copyright">
        <p>© (주) We@IT</p>
        <p class="version">Version 1.0.0</p>
      </div>
    </div>
  </footer>
  {/if}
  {/if}
  {#if $loginGate}
    <Modal label="로그인" on:close={() => finishLogin(false)}>
      <p class="login-reason">{$loginGate.reason}</p>
      <LoginPanel />
    </Modal>
  {/if}
</main>

<style>
  .reward-notice { display: flex; justify-content: center; align-items: center; gap: 20px; padding: 12px; background: #f2e7d8; color: #734319; }
  .reward-notice button { border: 0; color: inherit; background: transparent; font-size: 22px; cursor: pointer; }
  .login-reason { text-align: center; line-height: 1.7; }
  @font-face {
    font-family: 'HakgyoansimDunggeunmisoTTF-B';
    src: url('https://fastly.jsdelivr.net/gh/projectnoonnu/2408-5@1.0/HakgyoansimDunggeunmisoTTF-B.woff2') format('woff2');
    font-weight: 700;
    font-style: normal;
  }
  :global(body) {
    margin: 0;
    padding: 0;
    font-family: 'Pretendard Medium', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Oxygen, Ubuntu, Cantarell, 'Open Sans', 'Helvetica Neue', sans-serif;
    transition: background-color 0.6s ease, color 0.6s ease;
  }
  
  :global(*) {
    box-sizing: border-box;
    transition: background-color 0.6s ease, color 0.6s ease, border-color 0.6s ease, box-shadow 0.6s ease;
  }
  
  :global(body.dark) {
    background-color: #121212;
    color: #f5f5f5;
  }
  
  :global(body.light) {
    background-color: #f5f5f5;
    color: #404040;
  }
  
  :global(.theme-transition) {
    animation: themeTransition 0.6s ease;
  }
  
  @keyframes themeTransition {
    0% { opacity: 0.8; transform: scale(0.98); }
    50% { opacity: 0.9; transform: scale(1.01); }
    100% { opacity: 1; transform: scale(1); }
  }
  
  main {
    min-height: 100vh;
    display: flex;
    flex-direction: column;
  }
  
  /* 콘텐츠 영역 */
  .content {
    flex: 1;
    padding: 1rem;
    min-height: calc(100vh - 200px); /* 헤더와 푸터 높이를 고려한 최소 높이 */
    max-width: 1400px;
    margin: 0 auto;
    width: 100%;
  }

  /* 편집 페이지에서는 최대 너비와 여백 제거 */
  .content.editor-page {
    max-width: none;
    margin: 0;
    padding: 0;
    /* min-height: calc(100vh - 70px); 헤더 높이를 고려해 빈 공간 제거 */
  }

  .content.cocker-route {
    max-width: none;
    margin: 0;
    padding: 0;
    min-height: 100vh;
  }
  
  /* 푸터 스타일 */
  footer {
    text-align: center;
    padding: 1.5rem;
    transition: background-color 0.3s ease;
    position: relative;
    width: 100%;
    margin-top: 2rem;
  }
  
  .dark footer {
    background-color: #1a1a1a;
    color: #aaa;
    border-top: 1px solid rgba(255, 255, 255, 0.1);
  }
  
  .light footer {
    background-color: #e5e5e5;
    color: #666;
    border-top: 1px solid rgba(0, 0, 0, 0.1);
  }
  
  /* 푸터 스타일 */
  .footer-content {
    max-width: 1200px;
    margin: 0 auto;
    padding: 0 2rem;
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 1.5rem;
  }
  
  .footer-logo-img {
    height: 40px;
    width: auto;
  }
  
  .footer-links {
    display: flex;
    gap: 1.5rem;
    flex-wrap: wrap;
    justify-content: center;
  }
  
  
  .footer-link-btn {
    text-decoration: none;
    font-size: 0.9rem;
    transition: color 0.3s ease;
    background: none;
    border: none;
    padding: 0;
    cursor: pointer;
    font-family: inherit;
  }
  
  
  .dark .footer-link-btn {
    color: rgba(255, 255, 255, 0.7);
  }
  
  
  .light .footer-link-btn {
    color: rgba(0, 0, 0, 0.7);
  }
  
  
  .footer-link-btn:hover {
    color: #FF6382;
  }
  
  .footer-copyright {
    text-align: center;
    font-size: 0.8rem;
  }
  
  .dark .footer-copyright {
    color: rgba(255, 255, 255, 0.5);
  }
  
  .light .footer-copyright {
    color: rgba(0, 0, 0, 0.5);
  }
  
  .footer-copyright p {
    margin: 0.3rem 0;
  }
  
  .version {
    font-size: 0.7rem;
  }
  
  .dark .version {
    color: rgba(255, 255, 255, 0.3);
  }
  
  .light .version {
    color: rgba(0, 0, 0, 0.3);
  }
  
  @media (max-width: 768px) {
    .footer-content {
      padding: 0 1rem;
    }
    
    .footer-links {
      gap: 1rem;
    }
    
    .content {
      padding: 0.5rem;
    }
  }

  @media (orientation: portrait) and (max-width: 768px) {
    footer {
      display: none;
    }
  }

  .custom-link {
    text-decoration: none; /* 밑줄 제거 */
    color: inherit;        /* 부모의 글자색을 상속하거나 원하는 색상으로 지정 */
  }

  /* 마우스 오버(hover) 시 스타일 */
  .custom-link:hover {
    color: #0070f3;                     /* 마우스 올렸을 때 글자색 변경 */
  }
</style>
